import { useLayoutEffect, useRef, useState } from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "./tokens";
import { useIsPageChrome } from "../windowManager/pageChrome";
import { pageFullBarSpacerHeight } from "../windowManager/bottomBars";

const useStyles = makeStyles({
  root: {
    backgroundColor: BRAND.white,
    ...shorthands.borderTop("1px", "solid", "rgba(45,20,43,0.1)"),
    fontFamily: BRAND.fontPrimary,
    boxShadow: "0 -6px 18px rgba(45,20,43,0.06)",
  },
  rootSticky: {
    position: "sticky",
    // Über dem Fenster-Dock UND der mobilen Bottom-Leiste staffeln
    // (--wm-dock-h + --mobile-nav-h), Fallback 0 ohne Dock/Leiste.
    bottom: "calc(var(--wm-dock-h, 0px) + var(--mobile-nav-h, 0px))",
    zIndex: 50,
  },
  // VOLLSEITE: die Leiste löst sich aus der zentrierten Inhaltsspalte (max.
  // 880px) und läuft über die volle Breite am unteren Rand. Bezugsrahmen ist
  // die Fenster-Schale (FloatingWindow setzt dort `position: relative`) — der
  // nächste positionierte Vorfahr. Weil dieser Vorfahr AUSSERHALB des
  // scrollenden Fenster-Bodys liegt, scrollt die Leiste nicht mit und wird von
  // dessen `overflow` auch nicht beschnitten.
  rootPageFull: {
    position: "absolute",
    // Beginnt am rechten Rand des Inhaltsverzeichnisses, NICHT am Fensterrand:
    // sonst liefe die Leiste unter dem dunklen Verzeichnis durch und schnitte
    // es vor dem unteren Rand ab. Steht kein Verzeichnis (Hamburger-Modus,
    // andere Formulare), ist die Variable nicht gesetzt und die Leiste läuft
    // wie bisher über die volle Breite.
    left: "var(--toc-leiste-breite, 0px)",
    right: 0,
    bottom: "calc(var(--wm-dock-h, 0px) + var(--mobile-nav-h, 0px))",
    zIndex: 50,
  },
  // Platzhalter im Fluss: hält exakt so viel Höhe frei, wie die absolut
  // positionierte Leiste (plus Dock) verdeckt — sonst verschwände der letzte
  // Inhalt dahinter.
  pageFullSpacer: {
    flexShrink: 0,
  },
  progressTrack: {
    height: "4px",
    width: "100%",
    backgroundColor: "rgba(45,20,43,0.08)",
  },
  progressFill: {
    height: "4px",
    backgroundColor: BRAND.gold,
    transitionProperty: "width",
    transitionDuration: "300ms",
  },
  bar: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    rowGap: "10px",
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "16px",
    paddingRight: "16px",
    "@media (min-width: 720px)": {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingLeft: "24px",
      paddingRight: "24px",
    },
  },
  meta: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
  },
  label: {
    fontSize: "0.78rem",
    color: "rgba(45,20,43,0.7)",
  },
  savedAt: {
    fontSize: "0.72rem",
    color: BRAND.muted,
  },
  buttons: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    flexWrap: "wrap",
  },
  btn: {
    cursor: "pointer",
    appearance: "none",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    fontWeight: 700,
    paddingTop: "9px",
    paddingBottom: "9px",
    paddingLeft: "16px",
    paddingRight: "16px",
    ...shorthands.borderRadius("6px"),
    transitionProperty: "background-color, opacity",
    transitionDuration: "120ms",
    ":disabled": {
      cursor: "not-allowed",
      opacity: 0.4,
    },
  },
  btnSecondary: {
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.ink,
    ...shorthands.border("1px", "solid", "rgba(45,20,43,0.18)"),
    ":hover": {
      backgroundColor: "rgba(45,20,43,0.04)",
    },
  },
  btnGold: {
    backgroundColor: "rgba(247,168,35,0.15)",
    backgroundImage: "none",
    color: "#7a4c00",
    ...shorthands.border("1px", "solid", BRAND.gold),
    ":hover": {
      backgroundColor: "rgba(247,168,35,0.25)",
    },
  },
  btnPrimary: {
    backgroundColor: BRAND.purple,
    backgroundImage: "none",
    ...shorthands.border("1px", "solid", BRAND.purple),
    color: BRAND.white,
    ":hover": {
      backgroundColor: BRAND.ink,
      ...shorthands.borderColor(BRAND.ink),
    },
  },
  btnSubmit: {
    backgroundColor: BRAND.gold,
    backgroundImage: "none",
    ...shorthands.border("1px", "solid", BRAND.gold),
    color: BRAND.ink,
    ":hover": {
      backgroundColor: "#e89d20",
      ...shorthands.borderColor("#e89d20"),
    },
  },
});

const formatRelative = (iso: string | null): string => {
  if (!iso) return "";
  try {
    const t = new Date(iso).getTime();
    const diff = Math.floor((Date.now() - t) / 1000);
    if (diff < 5) return "gerade gespeichert";
    if (diff < 60) return `vor ${diff}s gespeichert`;
    if (diff < 3600) return `vor ${Math.floor(diff / 60)} min gespeichert`;
    return new Date(iso).toLocaleString("de-DE");
  } catch {
    return "";
  }
};

type Props = {
  onPrev: () => void;
  onNext: () => void;
  onSubmit: () => void;
  onSaveDraft: () => void;
  canPrev: boolean;
  canNext?: boolean;
  isLastStep: boolean;
  submitting?: boolean;
  /**
   * Absenden dauerhaft sperren, OHNE den Ladezustand vorzutäuschen: der
   * Vorgang ist bereits erfolgreich gespeichert, das Fenster steht aber noch
   * offen (z. B. weil ein Datei-Nachlauf gemeldet werden muss). `submitting`
   * dafür zu missbrauchen zeigte fälschlich „Speichere…".
   */
  submitDisabled?: boolean;
  savingDraft?: boolean;
  draftSavedAt?: string | null;
  progressLabel: string;
  progressFraction: number;
  submitLabel?: string;
  submittingLabel?: string;
  showSaveDraft?: boolean;
  nextDisabledTitle?: string;
  /** When true, pin the bar to the bottom of the viewport (position: fixed). */
  sticky?: boolean;
  /**
   * Weder „Weiter" noch „Absenden" anzeigen — für einen Schritt, der seinen
   * eigenen Abschluss-Knopf mitbringt (Beratermodus: „Auswertung & Abschluss").
   * Ohne das stünde dort ein „Weiter" ohne Ziel neben dem eigentlichen
   * Bestätigen-Knopf, und man müsste raten, welcher der beiden speichert.
   */
  hideForward?: boolean;
};

const BottomBar = ({
  onPrev,
  onNext,
  onSubmit,
  onSaveDraft,
  canPrev,
  canNext = true,
  isLastStep,
  submitting = false,
  submitDisabled = false,
  savingDraft = false,
  draftSavedAt = null,
  progressLabel,
  progressFraction,
  submitLabel = "Risikoanalyse absenden",
  submittingLabel = "Wird übermittelt…",
  showSaveDraft = true,
  nextDisabledTitle,
  sticky = false,
  hideForward = false,
}: Props) => {
  const styles = useStyles();
  const pct = Math.max(0, Math.min(1, progressFraction)) * 100;

  // In der Vollseite läuft die Leiste über die volle Breite (Auftraggeber,
  // 18.08.2026). Im Fenster bleibt alles wie bisher: sticky innerhalb der
  // Inhaltsspalte.
  const isPageChrome = useIsPageChrome();
  const fullWidth = sticky && isPageChrome;
  const barRef = useRef<HTMLElement | null>(null);
  const [barHeight, setBarHeight] = useState(0);
  useLayoutEffect(() => {
    const el = barRef.current;
    if (!fullWidth || !el) {
      setBarHeight(0);
      return;
    }
    const apply = () => setBarHeight(el.getBoundingClientRect().height);
    apply();
    // Die Höhe ändert sich mit der Breite (Umbruch der Knopfreihe < 720px).
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fullWidth]);

  const bar = (
    <footer
      ref={barRef}
      className={mergeClasses(
        styles.root,
        fullWidth ? styles.rootPageFull : sticky && styles.rootSticky,
      )}
    >
      <div className={styles.progressTrack}>
        <div className={styles.progressFill} style={{ width: `${pct}%` }} />
      </div>
      <div className={styles.bar}>
        <div className={styles.meta}>
          <span className={styles.label}>{progressLabel}</span>
          {draftSavedAt && (
            <span className={styles.savedAt}>· {formatRelative(draftSavedAt)}</span>
          )}
        </div>
        <div className={styles.buttons}>
          <button
            type="button"
            disabled={!canPrev || submitting}
            onClick={onPrev}
            className={mergeClasses(styles.btn, styles.btnSecondary)}
          >
            Zurück
          </button>
          {showSaveDraft && (
            <button
              type="button"
              disabled={submitting || savingDraft}
              onClick={onSaveDraft}
              className={mergeClasses(styles.btn, styles.btnGold)}
            >
              {savingDraft ? "Speichere…" : "Zwischenspeichern"}
            </button>
          )}
          {hideForward ? null : isLastStep ? (
            <button
              type="button"
              disabled={submitting || submitDisabled || !canNext}
              onClick={onSubmit}
              className={mergeClasses(styles.btn, styles.btnSubmit)}
              title={!canNext ? nextDisabledTitle : undefined}
            >
              {submitting ? submittingLabel : submitLabel}
            </button>
          ) : (
            <button
              type="button"
              disabled={submitting || !canNext}
              onClick={onNext}
              className={mergeClasses(styles.btn, styles.btnPrimary)}
              title={!canNext ? nextDisabledTitle : undefined}
            >
              Weiter
            </button>
          )}
        </div>
      </div>
    </footer>
  );

  // Die volle Breite entsteht durch Herauslösen aus dem Fluss — der Platzhalter
  // ersetzt als GESCHWISTER die freigewordene Höhe (inkl. Dock-Offset), damit
  // der letzte Inhalt nicht hinter der Leiste verschwindet.
  //
  // Bewusst als Fragment mit zwei festen Positionen (Platzhalter | Leiste)
  // statt als Wrapper um die Leiste: beim Umschalten Vollseite ↔ Fenster
  // wechselt so nur der erste Eintrag zwischen `false` und `<div>`, während die
  // Leiste an Position 1 stehen bleibt. Ein Wrapper hätte den Wurzelknoten
  // zwischen div und footer wechseln lassen → React remountet die Leiste
  // (Fokus auf „Weiter"/„Zurück" weg, Fortschritts-Animation startet neu).
  return (
    <>
      {fullWidth && (
        <div
          className={styles.pageFullSpacer}
          style={{ height: pageFullBarSpacerHeight(barHeight) }}
        />
      )}
      {bar}
    </>
  );
};

export default BottomBar;
